import React from 'react'
import { TextInput } from '../../components/form/textInput'
import { ControlledModal } from '../../layouts/components/modal'
import { Toasts } from '../../providers/toast'
import { makeRequest, showAxiosErrorAsToast } from '../../request'
import { Select } from '../../components/form/select'
import { Button } from '../../components/simple/button'
import { useLanguages } from '../../providers/languages'
import { Popover } from '../../layouts/components/popover'
import { DateTimePicker } from '../../components/form/timePicker'
import { roundUpToNearestMinute } from '../home/compose/scheduledTimePicker'
import { addHours, addMinutes, isAfter } from 'date-fns'
import { Popups } from '../../providers/popups'

export const GeneralLexikeetCallModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const languages = useLanguages()
  const languageOptions = React.useMemo(() => languages.getOptions((l) => !!l.lexikeetCode), [languages])
  const [language, setLanguage] = React.useState(languageOptions[0])
  const [email, setEmail] = React.useState('')
  const [emailInvalid, setEmailInvalid] = React.useState(false)
  const [phone, setPhone] = React.useState('')
  const [phoneInvalid, setPhoneInvalid] = React.useState(false)
  const [loading, setLoading] = React.useState(false)

  const onDemandAvailable = languages.find(language.value)?.lexikeetOnDemand

  const makeCall = (time?: Date) => {
    setLoading(true)
    setEmailInvalid(false)
    setPhoneInvalid(false)
    makeRequest<string>('/lkOnDemandNoStudent', {
      language: language.value,
      email,
      phoneNumber: phone,
      time,
    })
      .then((response) => {
        if (response == 'invalid-language') {
          // I dont think this should be possible
          Toasts.showToast({ message: 'Invalid language', error: true })
        } else if (response == 'invalid-email') {
          setEmailInvalid(true)
        } else if (response == 'invalid-phone') {
          setPhoneInvalid(true)
        } else {
          onClose()
          if (!time) {
            window.open(response, '_blank')
            Toasts.showToast({
              message: (
                <>
                  If your call did not automatically open in a new tab, click{' '}
                  <a target="_blank" href={response}>
                    here
                  </a>
                </>
              ),
              duration: 20000,
            })
          } else {
            Popups.confirm({
              title: <b>Call Scheduled</b>,
              message: 'You should receive an email with confirmation, and a google calendar invite soon.',
              yesText: 'Ok',
            })
          }
        }
      })
      .catch(showAxiosErrorAsToast)
      .finally(() => setLoading(false))
  }

  const [scheduleOpen, setScheduleOpen] = React.useState(false)
  const [scheduleTime, setScheduleTime] = React.useState<Date | undefined>(undefined)
  const minDate = React.useMemo(() => roundUpToNearestMinute(addMinutes(new Date(), 125)), [])

  return (
    <ControlledModal
      size="xs"
      onOpenChange={(o) => {
        if (!o) onClose()
      }}
      open={true}
      title="Make a general interpreted call"
      disabled={loading}
    >
      <div>Language</div>
      <Select options={languageOptions} value={language} onChange={setLanguage} blue />
      <div className="mt-2 font-bold">Add contact info to directly invite someone to your call</div>
      <div>Email (Optional)</div>
      <TextInput value={email} onChange={setEmail} invalid={emailInvalid} blue />
      <div>Phone number (Optional)</div>
      <TextInput value={phone} onChange={setPhone} invalid={phoneInvalid} blue />
      <div>When you press start, we will open your call in a new tab. You will be able to share the link</div>
      <div className="flex gap-2">
        <Button
          logo
          tooltip={onDemandAvailable ? undefined : 'This language is not available on-demand'}
          disabled={loading || !onDemandAvailable}
          className="self-start"
          onClick={() => makeCall()}
        >
          Start Call
        </Button>
        <Popover
          open={scheduleOpen && !loading}
          onOpenChange={(open) => {
            if (!open) setScheduleOpen(false)
          }}
          side="bottom"
          className="items-center gap-2 p-2"
          trigger={
            <Button logo disabled={loading} className="self-start" onClick={() => setScheduleOpen(true)}>
              Schedule Call
            </Button>
          }
        >
          <div className="text-lg font-semibold">Pick a date for this call</div>
          <DateTimePicker minDate={minDate} dateTime={scheduleTime} setDateTime={setScheduleTime} />
          {scheduleTime && (
            <Button
              logo
              onClick={async () => {
                if (
                  isAfter(scheduleTime, addHours(new Date(), 12)) ||
                  (await Popups.simpleConfirm(
                    'Since this time is so soon, an interpreter is not guaranteed to be available. Are you sure you want to schedule this call?'
                  ))
                )
                  makeCall(scheduleTime)
              }}
            >
              Send
            </Button>
          )}
        </Popover>
      </div>
    </ControlledModal>
  )
}
